/**
 * Per-record HIPAA access logger (Sprint J).
 *
 * Writes an `epcr_access_log` row for every meaningful event on an
 * ePCR: open, edit_field, sign, transmit, print, export, lock,
 * unlock, amend. The server-side trigger stamps client IP from
 * request.headers so events have provenance the client cannot fake.
 *
 * Usage:
 *   logEpcrAccess(epcrId, 'open');
 *   logEpcrAccess(epcrId, 'edit_field', { field: 'narrative_text' });
 *   logEpcrAccess(epcrId, 'sign', { role: 'emt' });
 *
 * Failures are non-fatal — the app must keep working even if the
 * log table is unreachable. Callers do not need to await; fire-and-
 * forget is correct for telemetry.
 */

import { supabase } from './supabase';
import { useAuthStore } from './auth-store';

export type EpcrAccessAction =
  | 'open' | 'edit_field' | 'sign' | 'transmit' | 'print'
  | 'export' | 'lock' | 'unlock' | 'amend';

let lastOpenLogged: { epcrId: string; at: number } | null = null;
const OPEN_DEDUPE_WINDOW_MS = 30_000;

function devMode(): boolean {
  return !import.meta.env.VITE_SUPABASE_URL;
}

export function logEpcrAccess(
  epcrId: string,
  action: EpcrAccessAction,
  meta: Record<string, unknown> = {},
): void {
  if (devMode()) return;

  // Dedupe rapid re-mounts of the same ePCR opening (React strict-mode
  // double-mounts, navigation thrash). Only one 'open' per 30s window.
  if (action === 'open') {
    const now = Date.now();
    if (lastOpenLogged && lastOpenLogged.epcrId === epcrId &&
        now - lastOpenLogged.at < OPEN_DEDUPE_WINDOW_MS) {
      return;
    }
    lastOpenLogged = { epcrId, at: now };
  }

  const user = useAuthStore.getState().user;
  void supabase.from('epcr_access_log').insert({
    epcr_id: epcrId,
    agency_id: user?.agency_id ?? null,
    user_id: user?.id ?? null,
    action,
    action_meta: meta,
    user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
  }).then(() => { /* noop */ }, () => { /* noop — never break the UI */ });
}

/**
 * Compose a SHA-256 hex string from arbitrary string inputs. Used by
 * the SignatureLedger to produce a tamper-evidence hash on each
 * captured signature: hash(attestation + name + signed_at).
 */
export async function sha256Hex(input: string): Promise<string> {
  if (typeof crypto === 'undefined' || !('subtle' in crypto)) return '';
  const buf  = new TextEncoder().encode(input);
  const hash = await crypto.subtle.digest('SHA-256', buf);
  return Array.from(new Uint8Array(hash))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}
